import React from "react";
import { BiLinkExternal } from "react-icons/bi";

export type CtaLinkState = {
  enabled: boolean;
  type: "INTERNAL" | "EXTERNAL";
  web: string;
  mobile: string;
  url: string;
};

export const emptyCtaLink: CtaLinkState = {
  enabled: false,
  type: "INTERNAL",
  web: "",
  mobile: "",
  url: "",
};

const BLOCKED_SCHEMES = ["javascript:", "data:", "vbscript:"];

const hasBlockedScheme = (value: string) =>
  BLOCKED_SCHEMES.some((scheme) =>
    value.trim().toLowerCase().startsWith(scheme)
  );

/**
 * Mirrors the backend rules so an invalid CTA never costs a round trip:
 * INTERNAL -> web is a path starting with a single "/", mobile is any route
 * that is not a javascript:/data:/vbscript: URL. EXTERNAL -> http(s) url.
 * Returns an error message, or null when the link is valid / not in use.
 */
export const validateCtaLink = (link: CtaLinkState): string | null => {
  if (!link.enabled) return null;

  if (link.type === "INTERNAL") {
    const web = link.web.trim();
    const mobile = link.mobile.trim();

    if (!web) return "Kindly add the web route for the CTA link";
    if (!web.startsWith("/") || web.startsWith("//")) {
      return "The web route must start with a single /";
    }
    if (!mobile) return "Kindly add the app route for the CTA link";
    if (hasBlockedScheme(mobile)) return "That app route is not allowed";
    return null;
  }

  const url = link.url.trim();
  if (!url) return "Kindly add the external link";
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return "The external link must start with http:// or https://";
    }
  } catch {
    return "Kindly add a valid external link";
  }
  return null;
};

/**
 * Builds the `link` payload, sending only the fields that belong to the
 * chosen type — the backend rejects fields of the other kind.
 */
export const buildCtaLink = (
  link: CtaLinkState
): { [key: string]: string } | undefined => {
  if (!link.enabled) return undefined;

  if (link.type === "INTERNAL") {
    return {
      type: "INTERNAL",
      web: link.web.trim(),
      mobile: link.mobile.trim(),
    };
  }
  return { type: "EXTERNAL", url: link.url.trim() };
};

/**
 * Renders a stored `link` in the history rows. Nothing is rendered when the
 * notification has no CTA (the backend returns `null` in that case).
 */
export const CtaLinkSummary = ({ link }: { link: any }) => {
  if (!link || !link.type) return null;

  const isInternal = link.type === "INTERNAL";
  const detail = isInternal
    ? [link.web && `web ${link.web}`, link.mobile && `app ${link.mobile}`]
        .filter(Boolean)
        .join("  ·  ")
    : link.url;

  if (!detail) return null;

  return (
    <div className="flex justify-start items-center gap-1 mt-1">
      <BiLinkExternal className="text-secondary_button text-[13px] shrink-0" />
      <h4 className="text-[12px] font-medium text-[#78C2F7] break-all">
        {isInternal ? "Internal" : "External"}
        {"  ·  "}
        {detail}
      </h4>
    </div>
  );
};

const inputClass =
  "w-full mt-2 text-[15px] tracking-wider placeholder:text-[15px] text-[#A0A9AD] bg-[#1C2227] px-4 outline-none h-[48px] rounded-xl border border-[#141414]";

const CtaLink = ({
  value,
  onChange,
}: {
  value: CtaLinkState;
  onChange: (next: CtaLinkState) => void;
}) => {
  const setType = (type: "INTERNAL" | "EXTERNAL") =>
    onChange({ ...value, type });

  return (
    <div className="w-full mt-6">
      <div className="flex justify-start w-full items-center gap-2">
        <input
          type="checkbox"
          checked={value.enabled}
          onChange={(e) =>
            onChange({ ...emptyCtaLink, enabled: e.target.checked })
          }
          className="h-[16px] w-[16px]"
        />
        <h4 className="text-[14px] font-bold text-white">
          Add call-to-action link
        </h4>
      </div>

      {value.enabled && (
        <div className="w-full mt-4">
          <div className="flex justify-start items-center gap-6">
            <div className="flex justify-start items-center gap-2">
              <input
                type="radio"
                name="ctaLinkType"
                checked={value.type === "INTERNAL"}
                onChange={() => setType("INTERNAL")}
                className="h-[16px] w-[16px]"
              />
              <h4 className="text-[14px] font-bold text-white">Internal</h4>
            </div>
            <div className="flex justify-start items-center gap-2">
              <input
                type="radio"
                name="ctaLinkType"
                checked={value.type === "EXTERNAL"}
                onChange={() => setType("EXTERNAL")}
                className="h-[16px] w-[16px]"
              />
              <h4 className="text-[14px] font-bold text-white">External</h4>
            </div>
          </div>

          {value.type === "INTERNAL" ? (
            <>
              <div className="w-full mt-4">
                <label className="text-[13px] font-bold text-white">
                  Web route
                </label>
                <input
                  type="text"
                  value={value.web}
                  onChange={(e) => onChange({ ...value, web: e.target.value })}
                  placeholder="/buy-crypto"
                  className={inputClass}
                />
              </div>
              <div className="w-full mt-4">
                <label className="text-[13px] font-bold text-white">
                  App route
                </label>
                <input
                  type="text"
                  value={value.mobile}
                  onChange={(e) =>
                    onChange({ ...value, mobile: e.target.value })
                  }
                  placeholder="buy/crypto"
                  className={inputClass}
                />
                <h4 className="font-medium text-[12px] text-[#475467] mt-2">
                  The app route can differ from the web route.
                </h4>
              </div>
            </>
          ) : (
            <div className="w-full mt-4">
              <label className="text-[13px] font-bold text-white">
                Link URL
              </label>
              <input
                type="text"
                value={value.url}
                onChange={(e) => onChange({ ...value, url: e.target.value })}
                placeholder="https://example.com"
                className={inputClass}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CtaLink;
